import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, memo } from 'react';

import { supportMessageLib } from 'entities/support-message';

import { ESupportMessageStatus } from 'shared/api';
import { LANGUAGE_OPTIONS } from 'shared/config';

type Props = {
  filter: string;
  status: ESupportMessageStatus | '';
  subject: string;
  language: string;
  totalCount: number;
  onFilterChange: (value: string) => void;
  onStatusChange: (value: ESupportMessageStatus | '') => void;
  onSubjectChange: (value: string) => void;
  onLanguageChange: (value: string) => void;
};

const statusLabels: Record<ESupportMessageStatus, string> = {
  [ESupportMessageStatus.New]: 'New',
  [ESupportMessageStatus.Read]: 'Read',
  [ESupportMessageStatus.Replied]: 'Replied',
};

export const SupportMessagesFilters = memo(
  ({
    filter,
    status,
    subject,
    language,
    totalCount,
    onFilterChange,
    onStatusChange,
    onSubjectChange,
    onLanguageChange,
  }: Props) => {
    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e.target.value);
    };

    const handleClearFilter = () => {
      onFilterChange('');
    };

    const handleStatusChange = (e: SelectChangeEvent) => {
      onStatusChange(e.target.value as ESupportMessageStatus | '');
    };

    const handleSubjectChange = (e: SelectChangeEvent) => {
      onSubjectChange(e.target.value);
    };

    const handleLanguageChange = (e: SelectChangeEvent) => {
      onLanguageChange(e.target.value);
    };

    return (
      <Paper sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" p="8px 16px" gap={2} flexWrap="wrap">
          <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>
            Support messages
          </Typography>

          <Box display="flex" alignItems="center" gap={2} flex={1} minWidth="300px">
            <TextField
              sx={{ minWidth: '350px', maxWidth: '500px', flex: 1 }}
              placeholder="Search by text, email..."
              size="small"
              value={filter}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton size="small" onClick={handleClearFilter}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={handleStatusChange}>
                <MenuItem value="">All</MenuItem>
                {Object.values(ESupportMessageStatus).map((s) => (
                  <MenuItem key={s} value={s}>
                    {statusLabels[s]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Subject</InputLabel>
              <Select value={subject} label="Subject" onChange={handleSubjectChange}>
                <MenuItem value="">All</MenuItem>
                {supportMessageLib.SUPPORT_SUBJECT_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {supportMessageLib.getSupportSubjectText(s)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Language</InputLabel>
              <Select value={language} label="Language" onChange={handleLanguageChange}>
                <MenuItem value="">All</MenuItem>
                {LANGUAGE_OPTIONS.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    {lang.name} ({lang.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
            {totalCount} {totalCount === 1 ? 'message' : 'messages'}
          </Typography>
        </Box>
      </Paper>
    );
  },
);

SupportMessagesFilters.displayName = 'SupportMessagesFilters';
